import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc,
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp,
  getDocs 
} from 'firebase/firestore';

const db = getFirestore();

class RealTimeAnalyticsAggregator {
  constructor() {
    this.listeners = [];
    this.cache = {
      activeSessions: 0,
      activeUsers: 0,
      pageViews: 0,
      recentActivity: [],
      popularPages: [],
      lastUpdated: null
    };
    this.subscribers = [];
    this.isInitialized = false;
  }

  // Initialize real-time listeners
  async init() {
    if (this.isInitialized) return;
    
    try {
      console.log('Initializing real-time analytics aggregator...');
      
      // Listen to real-time stats
      this.setupRealTimeStatsListener();
      
      // Listen to active sessions
      this.setupActiveSessionsListener();
      
      // Listen to recent page views
      this.setupRecentPageViewsListener();
      
      // Set up periodic cache updates
      this.setupPeriodicUpdates();
      
      this.isInitialized = true;
      console.log('Real-time analytics aggregator initialized');
    } catch (error) {
      console.error('Error initializing real-time analytics:', error);
    }
  }

  // Listen to real-time stats document
  setupRealTimeStatsListener() {
    try {
      const statsRef = doc(db, 'realTimeStats', 'current');
      
      const unsubscribe = onSnapshot(statsRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          this.cache.activeSessions = data.activeSessions || 0;
          this.cache.pageViews = data.pageViews || 0;
          this.cache.lastUpdated = new Date();
          
          this.notifySubscribers('realTimeStats', {
            activeSessions: this.cache.activeSessions,
            pageViews: this.cache.pageViews,
            lastUpdated: this.cache.lastUpdated
          });
        }
      }, (error) => {
        console.error('Error listening to real-time stats:', error);
      });
      
      this.listeners.push(unsubscribe);
    } catch (error) {
      console.error('Error setting up real-time stats listener:', error);
    }
  }

  // Listen to active sessions for real-time user count
  setupActiveSessionsListener() {
    try {
      // Use simpler query first, then filter in memory to avoid index requirement
      console.log('Setting up active sessions listener...');
      
      const q = query(
        collection(db, 'sessions_v2'),
        where('isActive', '==', true)
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const uniqueUsers = new Set();
        const uniqueVisitors = new Set();
        const sessionDetails = [];
        const userToVisitorMap = new Map(); // Track which visitors are authenticated users
        
        // Filter sessions to only include those active in the last 15 minutes
        const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
        let activeSessions = 0;
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          const lastActivity = data.lastActivity?.toDate();
          
          // Filter out sessions older than 15 minutes
          if (lastActivity && lastActivity > fifteenMinutesAgo) {
            activeSessions++;
            
            sessionDetails.push({
              id: doc.id,
              userId: data.userId,
              visitorId: data.visitorId,
              lastActivity: lastActivity,
              isActive: data.isActive
            });
            
            if (data.userId && data.userId !== 'anonymous') {
              uniqueUsers.add(data.userId);
              // Map this visitor to the authenticated user to avoid double counting
              if (data.visitorId) {
                userToVisitorMap.set(data.visitorId, data.userId);
              }
            }
            
            if (data.visitorId) {
              uniqueVisitors.add(data.visitorId);
            }
          }
        });
        
        // Count unique users: authenticated users + anonymous visitors (not already counted as users)
        const anonymousVisitors = new Set();
        uniqueVisitors.forEach(visitorId => {
          if (!userToVisitorMap.has(visitorId)) {
            anonymousVisitors.add(visitorId);
          }
        });
        
        this.cache.activeUsers = uniqueUsers.size + anonymousVisitors.size;
        this.cache.activeSessions = activeSessions;
        this.cache.lastUpdated = new Date();
        
        console.log(`🔥 Active sessions update:`, {
          totalSessions: snapshot.size,
          activeSessions: activeSessions,
          authenticatedUsers: uniqueUsers.size,
          anonymousVisitors: anonymousVisitors.size,
          totalActiveUsers: this.cache.activeUsers,
          sessionDetails: sessionDetails
        });
        
        this.notifySubscribers('activeUsers', {
          activeUsers: this.cache.activeUsers,
          activeSessions: this.cache.activeSessions,
          authenticatedUsers: uniqueUsers.size,
          anonymousVisitors: anonymousVisitors.size,
          lastUpdated: this.cache.lastUpdated
        });
      }, (error) => {
        console.error('Error listening to active sessions:', error);
      });
      
      this.listeners.push(unsubscribe);
    } catch (error) {
      console.error('Error setting up active sessions listener:', error);
    }
  }

  // Listen to recent page views for activity feed
  setupRecentPageViewsListener() {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      
      const q = query(
        collection(db, 'pageViews_v2'),
        where('timestamp', '>', Timestamp.fromDate(oneHourAgo)),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const recentActivity = [];
        const pageViewCounts = {};
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          
          // Add to recent activity with enhanced information including custom UID
          recentActivity.push({
            id: doc.id,
            pageName: data.pageName,
            timestamp: data.timestamp.toDate(),
            userId: data.userId === 'anonymous' ? null : data.userId,
            customUID: data.params?.userCustomUID || data.params?.customUID,
            userCustomUID: data.params?.userCustomUID,
            visitorId: data.visitorId,
            pageContext: data.params?.pageContext || null,
            ownerDisplayName: data.params?.ownerDisplayName || data.params?.userDisplayName,
            viewerType: data.params?.viewerType,
            specificPage: data.params?.specificPage || data.pageName
          });
          
          // Count page views for popular pages with enhanced data
          // Normalize page keys to prevent duplicates (same logic as ImprovedAnalyticsService)
          let pageKey = data.params?.specificPage || data.pageName || 'Unknown';
          const pageName = data.pageName || 'Unknown';
          
          // Normalize page names to consolidate similar pages
          if (pageKey === 'Hub' || pageKey.startsWith('Hub-')) {
            pageKey = 'Hub'; // Consolidate ALL hub variations
          } else if (pageKey === 'Registry' || pageKey.startsWith('Registry-')) {
            pageKey = 'Registry'; // Consolidate all registry pages
          } else if (pageKey === 'Events' || pageKey.startsWith('Events-')) {
            pageKey = 'Events'; // Consolidate all events pages
          } else if (pageKey === 'Admin' || pageKey.startsWith('Admin-')) {
            pageKey = 'Admin'; // Consolidate admin pages
          } else if (pageKey === 'Profile' || pageKey.startsWith('Profile-')) {
            pageKey = 'Profile'; // Consolidate profile pages
          }
          
          if (!pageViewCounts[pageKey]) {
            pageViewCounts[pageKey] = {
              count: 0,
              uniqueVisitors: new Set(),
              lastSeen: data.timestamp.toDate(),
              pageContext: data.params?.pageContext || null,
              ownerDisplayName: data.params?.ownerDisplayName || data.params?.userDisplayName,
              viewerType: data.params?.viewerType,
              specificPage: data.params?.specificPage || data.pageName,
              pageName: pageName, // Keep original page name for parsing
              userId: data.userId,
              userCustomUID: data.params?.userCustomUID || data.params?.customUID
            };
          }
          
          pageViewCounts[pageKey].count++;
          if (data.visitorId) {
            pageViewCounts[pageKey].uniqueVisitors.add(data.visitorId);
          }
          
          // Update last seen time and preserve latest owner info
          const currentTime = data.timestamp.toDate();
          if (currentTime > pageViewCounts[pageKey].lastSeen) {
            pageViewCounts[pageKey].lastSeen = currentTime;
            if (data.params?.ownerDisplayName || data.params?.userDisplayName) {
              pageViewCounts[pageKey].ownerDisplayName = data.params.ownerDisplayName || data.params.userDisplayName;
            }
            if (data.params?.pageContext) {
              pageViewCounts[pageKey].pageContext = data.params.pageContext;
            }
            if (data.params?.userCustomUID || data.params?.customUID) {
              pageViewCounts[pageKey].userCustomUID = data.params.userCustomUID || data.params.customUID;
            }
          }
        });
        
        // Convert page view counts to sorted array with enhanced data
        const popularPages = Object.entries(pageViewCounts)
          .map(([name, data]) => ({
            name,
            count: data.count,
            uniqueVisitors: data.uniqueVisitors.size,
            lastSeen: data.lastSeen,
            pageContext: data.pageContext,
            ownerDisplayName: data.ownerDisplayName,
            viewerType: data.viewerType,
            specificPage: data.specificPage,
            pageName: data.pageName, // Include original page name for debugging
            userId: data.userId,
            userCustomUID: data.userCustomUID
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
        

        
        this.cache.recentActivity = recentActivity;
        this.cache.popularPages = popularPages;
        this.cache.lastUpdated = new Date();
        
        this.notifySubscribers('recentActivity', {
          recentActivity: this.cache.recentActivity,
          popularPages: this.cache.popularPages,
          lastUpdated: this.cache.lastUpdated
        });
      }, (error) => {
        console.error('Error listening to recent page views:', error);
      });
      
      this.listeners.push(unsubscribe);
    } catch (error) {
      console.error('Error setting up recent page views listener:', error);
    }
  }

  // Set up periodic updates for aggregated data
  setupPeriodicUpdates() {
    // Update every 30 seconds
    this.periodicInterval = setInterval(() => {
      this.updateAggregatedData();
    }, 30000);
  }

  // Update aggregated data manually
  async updateAggregatedData() {
    try {
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Get today's stats
      const todayStats = await this.getTodayStats();
      
      // Get last 24 hours session metrics
      const sessionMetrics = await this.getSessionMetrics(last24Hours, now);
      
      const aggregatedData = {
        ...todayStats,
        ...sessionMetrics,
        lastUpdated: now
      };
      
      this.notifySubscribers('aggregatedData', aggregatedData);
    } catch (error) {
      console.error('Error updating aggregated data:', error);
    }
  }

  // Get today's statistics
  async getTodayStats() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateString = today.toISOString().split('T')[0];
      
      // Query today's stats from dailyStats_v2
      const statsDoc = doc(db, 'dailyStats_v2', dateString);
      const statsSnap = await getDoc(statsDoc);
      
      if (statsSnap.exists()) {
        const data = statsSnap.data();
        return {
          todayPageViews: data.pageViews || 0,
          todayUniqueVisitors: Object.keys(data.visitors || {}).length,
          todayActiveUsers: data.activeUsers?.length || 0,
          todayNewUsers: data.newUsers?.length || 0
        };
      }
      
      return {
        todayPageViews: 0,
        todayUniqueVisitors: 0,
        todayActiveUsers: 0,
        todayNewUsers: 0
      };
    } catch (error) {
      console.error('Error getting today stats:', error);
      return {
        todayPageViews: 0,
        todayUniqueVisitors: 0,
        todayActiveUsers: 0,
        todayNewUsers: 0
      };
    }
  }

  // Get session metrics for a time period
  async getSessionMetrics(startDate, endDate) {
    try {
      const q = query(
        collection(db, 'sessions_v2'),
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        where('startTime', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      
      let totalSessions = 0;
      let totalDuration = 0;
      let completedSessions = 0;
      let bounceSessions = 0;
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        totalSessions++;
        
        if (data.endTime) {
          completedSessions++;
          totalDuration += data.duration || 0;
          
          if ((data.pageViews || 0) <= 1) {
            bounceSessions++;
          }
        }
      });
      
      return {
        totalSessions,
        avgSessionDuration: completedSessions > 0 ? Math.round(totalDuration / completedSessions) : 0,
        bounceRate: completedSessions > 0 ? Math.round((bounceSessions / completedSessions) * 100) : 0
      };
    } catch (error) {
      console.error('Error getting session metrics:', error);
      return {
        totalSessions: 0,
        avgSessionDuration: 0,
        bounceRate: 0
      };
    }
  }

  // Subscribe to real-time updates
  subscribe(callback) {
    this.subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  // Notify all subscribers
  notifySubscribers(type, data) {
    this.subscribers.forEach(callback => {
      try {
        callback(type, data);
      } catch (error) {
        console.error('Error notifying subscriber:', error);
      }
    });
  }

  // Get current cached data
  getCurrentData() {
    return { ...this.cache };
  }

  // Clean up listeners
  cleanup() {
    try {
      this.listeners.forEach(unsubscribe => unsubscribe());
      this.listeners = [];
      
      if (this.periodicInterval) {
        clearInterval(this.periodicInterval);
        this.periodicInterval = null;
      }
      
      this.subscribers = [];
      this.isInitialized = false;
      
      console.log('Real-time analytics aggregator cleaned up');
    } catch (error) {
      console.error('Error cleaning up real-time analytics:', error);
    }
  }
}

// Export singleton instance
export const realTimeAnalytics = new RealTimeAnalyticsAggregator();
export default realTimeAnalytics;